/* =========================================================================
 * Antioch Collection View Switcher
 * -----------------------------------------------------------------------
 * Custom element that powers the "VIEW 1 / 2 / 3" toggle on the
 * collection page. The active view is stored on the <html> element as
 * `data-collection-view` so that all view-specific CSS lives in one place
 * and survives AJAX renders performed by facets.js.
 *
 * Responsibilities:
 *   - Read the initial view from the URL (?view=N), falling back to the
 *     value already set by the inline init script.
 *   - Update <html data-collection-view>, the URL, the hidden form input
 *     (so the choice is preserved across filter submissions) and the
 *     active button state when the user clicks a view button.
 *   - Sync the active button after facets.js re-renders the section.
 * ========================================================================= */

(function () {
  'use strict';

  const VALID_VIEWS = [1, 2, 3];
  const DEFAULT_VIEW_FALLBACK = 2;

  function parseView(value) {
    const parsed = parseInt(value, 10);
    return VALID_VIEWS.indexOf(parsed) === -1 ? null : parsed;
  }

  function resolveInitialView(defaultView) {
    let view = null;

    try {
      const params = new URLSearchParams(window.location.search);
      view = parseView(params.get('view'));
    } catch (error) {
      view = null;
    }

    if (view === null) {
      view = parseView(document.documentElement.getAttribute('data-collection-view'));
    }

    if (view === null) {
      view = parseView(defaultView);
    }

    return view || DEFAULT_VIEW_FALLBACK;
  }

  function updateUrl(view) {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('view', String(view));
      window.history.replaceState({ view: view }, '', url.toString());
    } catch (error) {
      /* no-op: history API failures must not break the UI */
    }
  }

  function getNextView(current) {
    const safe = parseView(current) || DEFAULT_VIEW_FALLBACK;
    return safe >= 3 ? 1 : safe + 1;
  }

  class CollectionViewSwitcher extends HTMLElement {
    constructor() {
      super();
      this.options = [];
      this.cycleButton = null;
      this.handleClick = this.handleClick.bind(this);
      this.handleCycleClick = this.handleCycleClick.bind(this);
      this.handlePopState = this.handlePopState.bind(this);
    }

    connectedCallback() {
      this.options = Array.from(this.querySelectorAll('[data-view-option]'));
      this.cycleButton = this.querySelector('[data-view-cycle]');

      const defaultView = parseInt(this.dataset.defaultView, 10) || DEFAULT_VIEW_FALLBACK;
      const initialView = resolveInitialView(defaultView);

      this.applyView(initialView, { updateHistory: true });

      this.options.forEach((option) => {
        option.addEventListener('click', this.handleClick);
      });

      if (this.cycleButton) {
        this.cycleButton.addEventListener('click', this.handleCycleClick);
      }

      window.addEventListener('popstate', this.handlePopState);
    }

    disconnectedCallback() {
      this.options.forEach((option) => {
        option.removeEventListener('click', this.handleClick);
      });

      if (this.cycleButton) {
        this.cycleButton.removeEventListener('click', this.handleCycleClick);
      }

      window.removeEventListener('popstate', this.handlePopState);
    }

    handleClick(event) {
      event.preventDefault();
      const target = event.currentTarget;
      const view = parseView(target.dataset.viewOption);
      if (!view) return;
      this.applyView(view, { updateHistory: true });
    }

    handleCycleClick(event) {
      event.preventDefault();
      const current = parseView(document.documentElement.getAttribute('data-collection-view'));
      const next = getNextView(current);
      this.applyView(next, { updateHistory: true });
    }

    handlePopState() {
      const defaultView = parseInt(this.dataset.defaultView, 10) || DEFAULT_VIEW_FALLBACK;
      const nextView = resolveInitialView(defaultView);
      this.applyView(nextView, { updateHistory: false });
    }

    updateCycleButton(view) {
      if (!this.cycleButton) return;

      const textEl = this.cycleButton.querySelector('[data-view-cycle-text]');
      const prefix = this.cycleButton.dataset.viewLabelPrefix || '';
      if (textEl) {
        textEl.textContent = prefix + String(view);
      }

      const activeOption = this.options.find(
        (option) => parseView(option.dataset.viewOption) === view
      );
      if (activeOption) {
        this.cycleButton.setAttribute('aria-label', activeOption.getAttribute('aria-label'));
      }
    }

    applyView(view, options) {
      const settings = options || {};
      const safeView = parseView(view) || DEFAULT_VIEW_FALLBACK;

      document.documentElement.setAttribute('data-collection-view', String(safeView));

      this.options.forEach((option) => {
        const optionView = parseView(option.dataset.viewOption);
        const isActive = optionView === safeView;
        option.classList.toggle('is-active', isActive);
        option.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      this.updateCycleButton(safeView);

      const hiddenInputs = document.querySelectorAll('[data-collection-view-input]');
      hiddenInputs.forEach((input) => {
        input.value = String(safeView);
      });

      if (settings.updateHistory) {
        updateUrl(safeView);
      }
    }
  }

  if (!customElements.get('collection-view-switcher')) {
    customElements.define('collection-view-switcher', CollectionViewSwitcher);
  }

  /* =======================================================================
   * Antioch Collection Filter Drawer
   * ---------------------------------------------------------------------
   * Custom element that turns the vertical filter form into a slide-in
   * drawer when the section setting "Enable filter as a drawer" is on.
   *
   *   - Opens on any [data-filter-drawer-open] click inside the section.
   *   - Closes on any [data-filter-drawer-close] click (overlay, X button,
   *     "VIEW RESULTS" button) or on ESC.
   *   - "CLEAN FILTERS" links continue to follow their href (collection
   *     URL with no params) — facets.js will intercept and re-render.
   *   - Survives AJAX re-renders done by facets.js because the section is
   *     replaced as a whole and the element re-initialises.
   * ===================================================================== */

  const BODY_LOCK_CLASS = 'filter-drawer-open';

  function findTrigger(drawer) {
    if (!drawer.id) return null;
    return document.querySelector('[data-filter-drawer-open][aria-controls="' + drawer.id + '"]');
  }

  class CollectionFilterDrawer extends HTMLElement {
    constructor() {
      super();
      this.handleTriggerClick = this.handleTriggerClick.bind(this);
      this.handleCloseClick = this.handleCloseClick.bind(this);
      this.handleBackdropClick = this.handleBackdropClick.bind(this);
      this.handleKeyDown = this.handleKeyDown.bind(this);
      this.handleDrawerClick = this.handleDrawerClick.bind(this);
    }

    connectedCallback() {
      // Reparent to <body> so the drawer escapes any sticky / transformed
      // ancestors that would otherwise trap its `position: fixed` inside
      // a stacking context and let the product grid render on top of it.
      this.relocateToBody();

      this.trigger = findTrigger(this);
      if (this.trigger) {
        this.trigger.addEventListener('click', this.handleTriggerClick);
      }

      this.closeElements = Array.from(this.querySelectorAll('[data-filter-drawer-close]'));
      this.addEventListener('click', this.handleCloseClick);

      // Safety net: clicks anywhere on the drawer surface that are NOT
      // inside the panel itself should also close the drawer. This makes
      // "click outside" work even if the overlay's own click listener
      // does not fire for some reason.
      this.addEventListener('click', this.handleBackdropClick);
      this.addEventListener('click', this.handleDrawerClick);

      document.addEventListener('keydown', this.handleKeyDown);
    }

    relocateToBody() {
      if (!document.body || this.parentElement === document.body) return;

      // Remove any stale instance left from a previous section render so we
      // do not end up with multiple drawers sharing the same id in <body>.
      if (this.id) {
        const cssEscape = window.CSS && typeof window.CSS.escape === 'function'
          ? window.CSS.escape
          : (value) => value.replace(/([^a-zA-Z0-9_\-])/g, '\\$1');
        const existing = document.body.querySelector('#' + cssEscape(this.id));
        if (existing && existing !== this) existing.remove();
      }
      document.body.appendChild(this);
    }

    disconnectedCallback() {
      if (this.trigger) {
        this.trigger.removeEventListener('click', this.handleTriggerClick);
      }
      this.removeEventListener('click', this.handleCloseClick);
      this.removeEventListener('click', this.handleBackdropClick);
      this.removeEventListener('click', this.handleDrawerClick);
      document.removeEventListener('keydown', this.handleKeyDown);

      if (this.classList.contains('is-open')) {
        document.documentElement.classList.remove(BODY_LOCK_CLASS);
        document.body.classList.remove(BODY_LOCK_CLASS);
      }
    }

    handleTriggerClick(event) {
      event.preventDefault();
      this.open();
    }

    handleCloseClick(event) {
      if (event.target.closest('[data-filter-drawer-apply]')) return;

      const closeControl = event.target.closest('[data-filter-drawer-close]');
      if (!closeControl || !this.contains(closeControl)) return;

      // The root element carries data-filter-drawer-close for backdrop clicks.
      // Panel interactions (e.g. accordion summaries) bubble here too — ignore those.
      if (closeControl === this) return;

      event.preventDefault();
      this.close();
    }

    handleBackdropClick(event) {
      if (!this.classList.contains('is-open')) return;
      const panel = this.querySelector('.filter-drawer__panel');
      if (panel && panel.contains(event.target)) return;
      event.preventDefault();
      this.close();
    }

    handleDrawerClick(event) {
      const applyButton = event.target.closest('[data-filter-drawer-apply]');
      if (!applyButton || !this.contains(applyButton)) return;

      event.preventDefault();
      event.stopPropagation();

      const facetForm = this.querySelector('facet-filters-form');
      if (facetForm && typeof facetForm.submitDeferredFilters === 'function') {
        facetForm.submitDeferredFilters();
      }

      this.close();
    }

    handleKeyDown(event) {
      if (event.key === 'Escape' && this.classList.contains('is-open')) {
        event.preventDefault();
        this.close();
      }
    }

    open() {
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add(BODY_LOCK_CLASS);
      document.body.classList.add(BODY_LOCK_CLASS);

      const panel = this.querySelector('.filter-drawer__panel');
      if (panel && typeof panel.focus === 'function') {
        requestAnimationFrame(() => panel.focus({ preventScroll: true }));
      }
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove(BODY_LOCK_CLASS);
      document.body.classList.remove(BODY_LOCK_CLASS);

      if (this.trigger && typeof this.trigger.focus === 'function') {
        this.trigger.focus({ preventScroll: true });
      }
    }
  }

  if (!customElements.get('collection-filter-drawer')) {
    customElements.define('collection-filter-drawer', CollectionFilterDrawer);
  }
})();
